import React from 'react';

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'default';
export type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  className = '',
}) => {
  const baseStyles = 'inline-flex items-center whitespace-nowrap font-medium rounded-pill';

  const variantStyles: Record<BadgeVariant, string> = {
    success: 'bg-status-success/10 text-status-success border border-status-success/20',
    warning: 'bg-status-warning/10 text-status-warning border border-status-warning/20',
    danger: 'bg-status-danger/10 text-status-danger border border-status-danger/20',
    info: 'bg-accent-primary/10 text-accent-primary border border-accent-primary/20',
    default: 'bg-surface-hover text-text-secondary border border-surface-secondary',
  };

  const sizeStyles: Record<BadgeSize, string> = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
    lg: 'text-sm px-3 py-1',
  };

  const dotColor: Record<BadgeVariant, string> = {
    success: 'bg-status-success',
    warning: 'bg-status-warning',
    danger: 'bg-status-danger',
    info: 'bg-accent-primary',
    default: 'bg-text-muted',
  };

  return (
    <span className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}>
      {dot && (
        <span className={`w-1.5 h-1.5 shrink-0 rounded-full mr-1.5 ${dotColor[variant]}`} />
      )}
      {children}
    </span>
  );
};

export default Badge;
